/**
 * Details You Will Love Carousel
 * Handles mobile carousel functionality with smooth sliding and touch support
 */

class DetailsCarousel {
  constructor(section, options = {}) {
    this.section = section;
    this.mobileBreakpoint = 767;
    this.ipadMinBreakpoint = 768;
    this.ipadMaxBreakpoint = 1024;
    // Force loop to false on mobile and iPad, regardless of the setting
    const shouldDisableLoop = this.shouldShowCarousel();
    this.options = {
      loop: shouldDisableLoop ? false : (options.loop !== undefined ? options.loop : true),
      autoplay: options.autoplay || false,
      autoplayDelay: options.autoplayDelay || 5000,
    };

    this.track = section.querySelector('[data-carousel-track]');
    this.cards = section.querySelectorAll('.carousel-card');
    this.prevBtn = section.querySelector('[data-carousel-prev]');
    this.nextBtn = section.querySelector('[data-carousel-next]');
    this.currentSlideEl = section.querySelector('[data-current-slide]');
    this.totalSlidesEl = section.querySelector('[data-total-slides]');

    if (!this.track || this.cards.length === 0) {
      return;
    }

    this.currentIndex = 0;
    this.totalSlides = this.cards.length;
    this.autoplayTimer = null;
    this.isDragging = false;
    this.startPos = 0;
    this.currentTranslate = 0;
    this.prevTranslate = 0;
    this.animationID = null;

    this.init();
  }

  init() {
    // Update total slides counter
    if (this.totalSlidesEl) {
      this.totalSlidesEl.textContent = this.totalSlides;
    }

    // Set up event listeners
    this.setupEventListeners();
    
    // Update UI
    this.updateCarousel();
    
    // Start autoplay if enabled
    if (this.options.autoplay) {
      this.startAutoplay();
    }

    // Listen for window resize to update loop option
    window.addEventListener('resize', () => this.handleResize());
  }

  handleResize() {
    // Force loop to false on mobile and iPad
    this.options.loop = this.shouldShowCarousel() ? false : this.options.loop;
    // Update button states
    this.updateCarousel();
  }

  /**
   * Check if carousel should be shown (mobile or iPad)
   */
  shouldShowCarousel() {
    const width = window.innerWidth;
    return width <= this.mobileBreakpoint || this.isIPadViewport();
  }

  /**
   * Check if current viewport is iPad (768px - 1024px)
   */
  isIPadViewport() {
    const width = window.innerWidth;
    return width >= this.ipadMinBreakpoint && width <= this.ipadMaxBreakpoint;
  }

  setupEventListeners() {
    // Button clicks
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.prev());
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.next());
    }

    // Touch events
    this.track.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: true });
    this.track.addEventListener('touchmove', this.handleTouchMove.bind(this), { passive: false }); // passive: false to allow preventDefault
    this.track.addEventListener('touchend', this.handleTouchEnd.bind(this));

    // Mouse events (for desktop dragging)
    this.track.addEventListener('mousedown', this.handleMouseDown.bind(this));
    this.track.addEventListener('mousemove', this.handleMouseMove.bind(this));
    this.track.addEventListener('mouseup', this.handleMouseUp.bind(this));
    this.track.addEventListener('mouseleave', this.handleMouseLeave.bind(this));

    // Prevent context menu on long press
    this.track.addEventListener('contextmenu', (e) => {
      if (this.isDragging) {
        e.preventDefault();
      }
    });
  }

  handleTouchStart(e) {
    this.isDragging = true;
    this.startPos = e.touches[0].clientX;
    this.startPosY = e.touches[0].clientY;
    this.touchStartTime = Date.now();
    this.isScrolling = undefined; // undefined = not determined yet
    this.isMoved = false;
    
    // Set touch-action via JS as fallback
    this.track.style.touchAction = 'pan-y';
  }

  handleTouchMove(e) {
    if (!this.isDragging) return;
    
    const currentX = e.touches[0].clientX;
    const currentY = e.touches[0].clientY;
    const diffX = currentX - this.startPos;
    const diffY = currentY - this.startPosY;
    
    // Determine scroll direction on first move (like Swiper does)
    if (typeof this.isScrolling === 'undefined') {
      // Use angle-based detection like Swiper
      // touchAngle of 45 degrees means if angle is less than 45°, it's horizontal
      const touchAngle = Math.atan2(Math.abs(diffY), Math.abs(diffX)) * 180 / Math.PI;
      this.isScrolling = touchAngle > 45; // true = vertical scrolling, false = horizontal swiping
    }
    
    // If horizontal swipe detected, prevent vertical scrolling
    if (!this.isScrolling) {
      if (e.cancelable) {
        e.preventDefault();
        e.stopPropagation();
      }
      this.isMoved = true;
      
      // Temporarily disable touch-action for smoother horizontal swipe
      this.track.style.touchAction = 'none';
    }
    
    // Add some resistance at boundaries if loop is disabled
    if (!this.options.loop && !this.isScrolling) {
      if ((this.currentIndex === 0 && diffX > 0) || 
          (this.currentIndex === this.totalSlides - 1 && diffX < 0)) {
        return;
      }
    }
  }

  handleTouchEnd(e) {
    if (!this.isDragging) return;
    
    const endPos = e.changedTouches[0].clientX;
    const diff = endPos - this.startPos;
    const threshold = 50; // Minimum swipe distance
    const timeDiff = Date.now() - this.touchStartTime;
    
    // Restore touch-action
    this.track.style.touchAction = 'pan-y';

    // Only change slide if it was a horizontal swipe (not scrolling)
    if (!this.isScrolling && this.isMoved && Math.abs(diff) > threshold) {
      // Fast swipe detection - lower threshold for quick swipes
      const isQuickSwipe = timeDiff < 300 && Math.abs(diff) > 20;
      
      if (isQuickSwipe || Math.abs(diff) > threshold) {
        if (diff > 0) {
          this.prev();
        } else {
          this.next();
        }
      }
    }

    this.isDragging = false;
    this.isScrolling = undefined;
    this.isMoved = false;
  }

  handleMouseDown(e) {
    this.isDragging = true;
    this.startPos = e.clientX;
    this.track.style.cursor = 'grabbing';
    e.preventDefault();
  }

  handleMouseMove(e) {
    if (!this.isDragging) return;
    e.preventDefault();
  }

  handleMouseUp(e) {
    if (!this.isDragging) return;
    
    const endPos = e.clientX;
    const diff = endPos - this.startPos;
    const threshold = 50;

    if (Math.abs(diff) > threshold) {
      if (diff > 0) {
        this.prev();
      } else {
        this.next();
      }
    }

    this.isDragging = false;
    this.track.style.cursor = 'grab';
  }

  handleMouseLeave() {
    if (this.isDragging) {
      this.isDragging = false;
      this.track.style.cursor = 'grab';
    }
  }

  next() {
    // For iPad, max slide is totalSlides - 2 (show 2 cards at a time)
    const maxSlide = this.isIPadViewport() ? Math.max(0, this.totalSlides - 2) : this.totalSlides - 1;
    if (this.currentIndex < maxSlide) {
      this.currentIndex++;
    } else if (this.options.loop) {
      this.currentIndex = 0;
    } else {
      return; // Don't advance if at end and loop is disabled
    }
    this.updateCarousel();
  }

  prev() {
    if (this.currentIndex > 0) {
      this.currentIndex--;
    } else if (this.options.loop) {
      this.currentIndex = this.totalSlides - 1;
    } else {
      return; // Don't go back if at start and loop is disabled
    }
    this.updateCarousel();
  }

  goToSlide(index) {
    if (index >= 0 && index < this.totalSlides) {
      this.currentIndex = index;
      this.updateCarousel();
    }
  }

  updateCarousel() {
    // Calculate the offset - show part of the next card
    const card = this.cards[this.currentIndex];
    const cardWidth = card.offsetWidth;
    const trackWidth = this.track.offsetWidth;
    const distance = trackWidth - cardWidth;
    
    const gap = 12;
    const offset = 20; // Show 20px of the next card
    
    let translateX = 0;
    
    // For iPad, show 2 cards at a time with different calculation
    if (this.isIPadViewport()) {
      const maxSlide = Math.max(0, this.totalSlides - 2);
      if (this.currentIndex >= maxSlide) {
        // Last position - show last 2 cards
        translateX = -(maxSlide * (cardWidth + gap));
      } else {
        translateX = -(this.currentIndex * (cardWidth + gap));
      }
    } else {
      // Mobile - single card view
      // Calculate transform to show current card and part of next card
      if (this.currentIndex === this.totalSlides - 1) {
        translateX = -(this.currentIndex * (cardWidth + gap)) + distance;
      } else {
        translateX = -(this.currentIndex * (cardWidth + gap)) + offset;
      }
      
      // Don't offset on the first slide
      if (this.currentIndex === 0) {
        translateX = 0;
      }
    }
    
    this.track.style.transform = `translateX(${translateX}px)`;

    // Update counter
    if (this.currentSlideEl) {
      this.currentSlideEl.textContent = this.currentIndex + 1;
    }

    // Update button states if loop is disabled
    if (!this.options.loop) {
      // For iPad, max slide is totalSlides - 2 (show 2 cards at a time)
      const maxSlide = this.isIPadViewport() ? Math.max(0, this.totalSlides - 2) : this.totalSlides - 1;
      if (this.prevBtn) {
        this.prevBtn.disabled = this.currentIndex === 0;
      }
      if (this.nextBtn) {
        this.nextBtn.disabled = this.currentIndex === maxSlide;
      }
    } else {
      // Enable all buttons if loop is enabled
      if (this.prevBtn) this.prevBtn.disabled = false;
      if (this.nextBtn) this.nextBtn.disabled = false;
    }
  }
}

// Export to global scope
window.DetailsCarousel = DetailsCarousel;


$(window).ready(function() {

  function initIntersectionObservers() {
    // .details-container - triggerHook: 0.9 相当于 rootMargin: '-10%'
    const detailsContainer = document.querySelector('.details-container');
    if (detailsContainer) {
      const containerObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            detailsContainer.classList.add('active');
            containerObserver.unobserve(detailsContainer);
          }
        });
      }, {
        threshold: 0,
        rootMargin: '-10% 0px 0px 0px'
      });
      containerObserver.observe(detailsContainer);
    }

    // .details-grid - triggerHook: 0.8 相当于 rootMargin: '-20%'
    const detailsGrid = document.querySelector('.details-grid');
    if (detailsGrid) {
      const gridObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            detailsGrid.classList.add('active');
            gridObserver.unobserve(detailsGrid);
          }
        });
      }, {
        threshold: 0,
        rootMargin: '-20% 0px 0px 0px'
      });
      gridObserver.observe(detailsGrid);
    }
  }

  initIntersectionObservers();
})